import { omit } from 'lodash'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useAuthStore } from '@/modules/auth/stores/auth-store'
import { useFetch } from '@/modules/core/composables/useFetch'
import { useNotificationStore } from '@/modules/shared/stores/notification-store'
import { useFilingLocatorV4Service } from '../services/filing-locator-v4-service'
import { useEntitySyncService } from '../services/entity-sync-service'

interface Links {
  [key: string]: string
}

interface Investor {
  id: number | string
  type: string
  name: string
  has_tax_documents: boolean
  tax_id: string
  has_tax_id?: boolean
}

interface Entity {
  id: number | string
  administrator: string
  formation_date: string
  investors: Investor[]
  legal_name: string
  links: Links
  name: string
  tax_id: string
  type: string
}
export interface FilingLocator {
  id: number | string
  uuid?: string
  approved_for_filing_at: string
  entity: Entity
  finalized_return_document_url: string
  is_final_return: boolean
  prepared_return_document_url: string
  rejected_for_filing_at: string
  return_status: string
  tax_year: number
}

export const useFilingLocatorStore = defineStore('FilingLocator', () => {
  const authStore = useAuthStore()
  const route = useRoute()
  const notificationStore = useNotificationStore()

  const v4Service = useFilingLocatorV4Service({
    headers: {
      Authorization: `Bearer ${authStore.access_token}`,
    },
    route,
  })

  const entitySyncService = useEntitySyncService({
    headers: {
      Authorization: `Bearer ${authStore.access_token}`,
    },
    route,
  })

  /////////////////////////////////////////////////////////////////////////////
  // STATE
  /////////////////////////////////////////////////////////////////////////////

  // const mockItems: FilingLocator[] = [
  //   {
  //     id: 1,
  //     uuid: 'xyz',
  //     tax_year: 2022,
  //     is_final_return: false,
  //     return_status: 'not_ready',

  //     entity: {
  //       id: '1',
  //       administrator: 'John Doe',
  //       formation_date: '2020-01-01',
  //       investors: [
  //         {
  //           id: 1,
  //           type: 'Investor',
  //           name: 'LeBraun Premo',
  //           tax_id: '123456789',
  //         },
  //         {
  //           id: 2,
  //           type: 'Investor',
  //           name: 'Rachael Qualls',
  //           tax_id: null,
  //         },
  //       ],
  //       legal_name: 'XYZ LLC',
  //       links: {
  //         url: '#',
  //       },
  //       name: 'XYZ',
  //       tax_id: null,
  //       type: 'LLC',
  //     },
  //   },
  // ]

  const items = ref<FilingLocator[]>([])
  const item = ref<FilingLocator>()
  const availableYears = ref<number[]>([])
  const polling = ref(false)
  const pollingError = ref<string | null>(null)
  const lastYear = ref<any>(null)
  const lastAdministered = ref<boolean>(true)

  const uploadProgress = ref({
    active: false,
    done: false,
    entityName: '',
    current: 0,
    total: 0,
    failedFiles: [] as string[],
  })

  const dismissUpload = () => {
    uploadProgress.value = { active: false, done: false, entityName: '', current: 0, total: 0, failedFiles: [] }
  }

  /////////////////////////////////////////////////////////////////////////////
  // GETTERS
  /////////////////////////////////////////////////////////////////////////////

  // prettier-ignore
  const company_k1_options = {
    'Not required': 'not-required',
    'Not received': 'not-received',
    'Received': 'received',
  }

  const entity_types = [
    'C-Corp',
    'LLC',
    'LLP',
    'LP',
    'Ltd',
    'Non-Profit Corporation',
    'Other',
    'Pte Ltd',
    'Pty Ltd',
    'S-Corp',
    'Trust',
  ]

  const is_empty = computed(() => items.value.length === 0)

  const is_finalized = computed(
    () =>
      items.value.length > 0 &&
      items.value.every((item) => ['ready_to_file', 'submitted', 'distributed'].includes(item.return_status)),
  )

  const return_statuses = [
    {
      text: 'Not ready',
      value: 'not_ready',
      disabled: true,
    },
    {
      text: 'Not required',
      value: 'not_required',
      disabled: true,
    },
    {
      text: 'Disregarded entity',
      value: 'disregarded_entity',
      disabled: true,
    },
    {
      text: 'Extended',
      value: 'extended',
      disabled: true,
    },
    {
      text: 'Rough estimate',
      value: 'rough_estimate',
      disabled: true,
    },
    {
      text: 'Needs approval',
      value: 'needs_approval',
      disabled: true,
    },
    {
      text: 'Rejected for filing',
      value: 'rejected_for_filing',
      disabled: true,
    },
    {
      text: 'Approved for filing',
      value: 'approved_for_filing',
      disabled: true,
    },
    {
      text: 'Submitted',
      value: 'submitted',
      disabled: true,
    },
    {
      text: 'Distributed',
      value: 'distributed',
      disabled: true,
    },
  ]

  /////////////////////////////////////////////////////////////////////////////
  // UTILS
  /////////////////////////////////////////////////////////////////////////////

  const handle_error = (err: any) => {
    // TODO handle error
    console.error(err)
  }

  const updateItem = (item: FilingLocator, id: number | string, updates: any) => {
    if (item.id === id) {
      return {
        ...item,
        ...updates,
      }
    }
    return item
  }

  /////////////////////////////////////////////////////////////////////////////
  // POLLING
  /////////////////////////////////////////////////////////////////////////////

  const pollForResults = async (jobId: string) => {
    const MAX_ATTEMPTS = 60 // 60 × 2s = 2 minutes max
    const POLL_INTERVAL = 2000

    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
      await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL))

      try {
        const data = await useFetch(
          `/v4/${route.params.slug}/tax/filing-locators/status?job_id=${encodeURIComponent(jobId)}`,
        ).get<{ status: string; data?: any }>()

        if (data.status === 'success') {
          items.value = data.data
          polling.value = false
          return
        }

        if (data.status === 'error') {
          polling.value = false
          pollingError.value = data.data?.reason || 'Failed to load tax entities.'
          return
        }

        // status === 'pending' → continue polling
      } catch (err) {
        polling.value = false
        pollingError.value = 'Failed to load tax entities. Please try again.'
        return
      }
    }

    // Timeout
    polling.value = false
    pollingError.value = 'Request timed out. Please refresh the page.'
  }

  /////////////////////////////////////////////////////////////////////////////
  // ACTIONS
  /////////////////////////////////////////////////////////////////////////////

  const actions = {
    attach_finalized_return_document: async (id: number | string, file: FileList) => {
      const [err, _data] = await v4Service.attach_finalized_return_document(id, {}, file)

      if (err) return handle_error(err)

      await actions.retrieve(id)
    },

    attach_investor_return_document: async (id: number | string, files: FileList, entityName = '') => {
      if (uploadProgress.value.active) return

      uploadProgress.value = {
        active: true,
        done: false,
        entityName,
        current: 0,
        total: files.length,
        failedFiles: [],
      }

      for (let i = 0; i < files.length; i++) {
        const [err, _data] = await v4Service.attach_investor_return_document(id, {}, [files[i]])

        if (err) {
          uploadProgress.value.failedFiles.push(files[i].name)
        }

        uploadProgress.value.current = i + 1
      }

      uploadProgress.value.done = true

      await actions.retrieve(id)
    },

    attach_prepared_return_document: async (id: number | string, file: FileList) => {
      const [err, _data] = await v4Service.attach_prepared_return_document(id, {}, file)

      if (err) return handle_error(err)

      await actions.retrieve(id)
      notificationStore.enqueue('success', 'Prepared return was successfully attached')
    },

    approve_prepared_return: async (id: number | string) => {
      const [err, _data] = await v4Service.approve_prepared_return(id)

      if (err) return handle_error(err)

      await actions.retrieve(id)
    },

    create_efile: async (id: number | string) => {
      const [err, _data] = await entitySyncService.create_efile(id)

      if (err) return handle_error(err)

      await actions.retrieve(id)
    },

    efiles_validation: async (id: number | string) => {
      const [err, _data] = await entitySyncService.efiles_validation(id)

      if (err) return handle_error(err)

      await actions.retrieve(id)
    },

    download_return: async (id: number | string) => {
      const [err, _data] = await entitySyncService.download_return(id)

      if (err) return handle_error(err)

      await actions.retrieve(id)
    },

    import_entity: async (id: number | string) => {
      const [err, _data] = await entitySyncService.import_entity(id)

      if (err) return handle_error(err)

      await actions.retrieve(id)
    },

    print_return: async (id: number | string) => {
      const [err, _data] = await entitySyncService.print_return(id)

      if (err) return handle_error(err)

      await actions.retrieve(id)
    },

    recall_k1s: async (id: number | string) => {
      const [err, _data] = await v4Service.recall_k1s(id)

      if (err) return handle_error(err)

      await actions.retrieve(id)
    },

    reject_prepared_return: async (id: number | string) => {
      const [err, _data] = await v4Service.reject_prepared_return(id)

      if (err) return handle_error(err)

      await actions.retrieve(id)
    },

    remove_finalized_return_document: async (id: number | string) => {
      const [err, _data] = await v4Service.remove_finalized_return_document(id)

      if (err) return handle_error(err)

      await actions.retrieve(id)
    },

    remove_prepared_return_document: async (id: number | string) => {
      const [err, _data] = await v4Service.remove_prepared_return_document(id)

      if (err) return handle_error(err)

      await actions.list(lastYear.value, lastAdministered.value)
    },

    list: async (year, administered: boolean = true) => {
      lastYear.value = year
      lastAdministered.value = administered
      polling.value = true
      pollingError.value = null

      try {
        const data = await useFetch(
          `/v4/${route.params.slug}/tax/filing-locators/list?tax_year=${year}&administered=${administered}`,
        ).get<{ status: string; data?: any[]; job_id?: string }>()

        // Cache hit — data returned immediately
        if (data.status === 'success' && data.data) {
          items.value = data.data
          polling.value = false
          return
        }

        // 202 pending — poll for results
        if (data.status === 'pending' && data.job_id) {
          await pollForResults(data.job_id)
        }
      } catch (err) {
        polling.value = false
        handle_error(err)
      }
    },

    fetchYears: async () => {
      try {
        availableYears.value = await useFetch(`/v4/${route.params.slug}/tax/filing-locators/years`).get<number[]>()
      } catch (err) {
        handle_error(err)
      }
    },

    mark_as_final_return: async (id: number | string) => {
      const [err, _data] = await v4Service.mark_as_final_return(id)

      if (err) return handle_error(err)

      items.value = items.value.map((item) => updateItem(item, id, { is_final_return: true }))
    },

    set_final_return: async (id: number | string, final_return: boolean) => {
      const [err, _data] = await v4Service.set_final_return(id, { final_return })

      if (err) return handle_error(err)

      items.value = items.value.map((item) => updateItem(item, id, { final_return }))
    },

    set_return_status: async (id: number | string, return_status: string) => {
      const [err, _data] = await v4Service.set_return_status(id, { return_status })

      if (err) return handle_error(err)

      items.value = items.value.map((item) => updateItem(item, id, { return_status }))
    },

    // NOTE: This action triggers the mark_as_final_return and unmark_as_final_return actions,
    // but is not directly invoked from the front end.
    toggle_is_final_return: async (id: number | string, checked: boolean) => {
      if (checked) {
        await actions.mark_as_final_return(id)
      } else {
        await actions.unmark_as_final_return(id)
      }
    },

    unmark_as_final_return: async (id: number | string) => {
      const [err, _data] = await v4Service.unmark_as_final_return(id)

      if (err) return handle_error(err)

      items.value = items.value.map((item) => updateItem(item, id, { is_final_return: false }))
    },

    patch: async (store_filing_locator: FilingLocator) => {
      const entity = {
        name: store_filing_locator.entity_name,
        legal_name: store_filing_locator.entity_legal_name,
        tax_id: store_filing_locator.tax_id,
        type: store_filing_locator.type,
        formation_date: store_filing_locator.formation_date,
      }
      const investors = store_filing_locator.investors
      try {
        await useFetch(
          `/v4/${route.params.slug}/tax/filing-locators/${store_filing_locator.id}/update-entity-information`,
        ).post({ entity, investors })
        items.value = items.value.map((item) => updateItem(item, store_filing_locator.id, store_filing_locator))
      } catch (err) {
        handle_error(err)
      }
    },

    retrieve: async (id: number | string) => {
      try {
        const response = await useFetch(`/v4/${route.params.slug}/tax/filing-locators/${id}/details`).get<{
          status: string
          data: FilingLocator
        }>()

        const index = items.value.findIndex((item) => item.id === id)
        items.value[index] = response.data
      } catch (err) {
        handle_error(err)
      }
    },

    select: async (id: number | string) => {
      const selected = items.value.find((item) => item.id === id)
      item.value = { ...selected }

      // Fetch full data from details endpoint to get actual tax_ids
      // (list data omits tax_ids for performance)
      try {
        const response = await useFetch(`/v4/${route.params.slug}/tax/filing-locators/${id}/details`).get<{
          status: string
          data: FilingLocator
        }>()
        item.value = response.data
      } catch (err) {
        handle_error(err)
      }
    },
  }

  /////////////////////////////////////////////////////////////////////////////
  // RETURN
  /////////////////////////////////////////////////////////////////////////////

  return {
    items,
    item,
    availableYears,
    polling,
    pollingError,
    uploadProgress,
    dismissUpload,
    entity_types,
    is_empty,
    is_finalized,
    return_statuses,
    ...actions,
  }
})
